import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  computed,
  signal,
} from '@angular/core';
import { IonSearchbar } from '@ionic/angular/standalone';
import CountryList from 'country-list-with-dial-code-and-flag';
import { SexyModule } from '@qid/sexy';

/** ISO 3166-1 alpha-2 country code (e.g. `IN`, `US`). */
export type IsoCountryCode = string;

export interface CountrySelectorOption {
  isoCode: IsoCountryCode;
  name: string;
  dialCode: string;
  flag: string;
  localName: string;
}

@Component({
  selector: 'country-selector',
  standalone: true,
  templateUrl: './country-selector.component.html',
  styleUrls: ['./country-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, SexyModule, IonSearchbar],
})
export class CountrySelectorComponent {
  @Input() set countryCode(value: IsoCountryCode | null | undefined) {
    this._countryCode = (value ?? '').toString().toUpperCase();
  }
  get countryCode(): IsoCountryCode {
    return this._countryCode;
  }
  private _countryCode = '';

  @Output() readonly countryCodeChange = new EventEmitter<IsoCountryCode>();
  /** Full row when you need name, dial code, or flag after pick. */
  @Output() readonly selectionChange = new EventEmitter<CountrySelectorOption>();

  @Input() searchPlaceholder = 'Search country or code';
  @Input() disabled = false;

  protected readonly searchQuery = signal('');

  private static buildRows(): CountrySelectorOption[] {
    const indiaCode = 'IN';
    return CountryList.getAll({ withSecondary: false })
      .map((c) => ({
        isoCode: c.code.toUpperCase(),
        name: c.name,
        dialCode: c.dialCode,
        flag: c.flag,
        localName: c.localName ?? '',
      }))
      .sort((a, b) => {
        const aIndia = a.isoCode === indiaCode;
        const bIndia = b.isoCode === indiaCode;
        if (aIndia !== bIndia) {
          return aIndia ? -1 : 1;
        }
        return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
      });
  }

  private readonly countries = CountrySelectorComponent.buildRows();

  protected readonly filteredCountries = computed(() => {
    const q = this.searchQuery().trim().toLowerCase().replace(/\s+/g, ' ');
    if (!q) {
      return this.countries;
    }
    return this.countries.filter((c) => {
      const dial = c.dialCode.replace(/\s/g, '').toLowerCase();
      return (
        c.name.toLowerCase().includes(q) ||
        c.isoCode.toLowerCase().includes(q) ||
        dial.includes(q.replace(/^\+/, '')) ||
        (c.localName && c.localName.toLowerCase().includes(q))
      );
    });
  });

  protected onSearch(ev: CustomEvent<{ value?: string }>): void {
    this.searchQuery.set(ev.detail?.value ?? '');
  }

  protected select(row: CountrySelectorOption): void {
    if (this.disabled) {
      return;
    }
    this._countryCode = row.isoCode;
    this.countryCodeChange.emit(row.isoCode);
    this.selectionChange.emit(row);
  }

  protected isSelected(row: CountrySelectorOption): boolean {
    return !!this._countryCode && row.isoCode === this._countryCode;
  }
}
